import {
  AutoCompleteMulti,
  FormContainer,
  ISelectType,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { INewNotification } from "../../../../../models/notifications";
import MainApi from "../../../../../services/mainApi.service";
import {
  createNotification,
  getNotificationById,
  updateNotificationById,
} from "../../../../../services/notification.service";
import SitesAppsService from "../../../../../services/sitesApps.service";
import NotificationBatchActions from "./NotificationBatchActions";
import NotificationContents from "./NotificationContents/NotificationContents";
import NotificationFilters from "./NotificationFilters/NotificationFilters";
import NotificationInformations from "./NotificationInformations";
import { TNotificationForm, notificationSchema } from "./NotificationSchema";
import { NotificationTypeSelect } from "./NotificationTypeSelect/NotificationTypeSelect";

const translationPath = "page.register.notifications.";

const siteAppExtensionService = new SitesAppsService();

const LANGUAGE_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Language/";
const languageService = new MainApi<ISelectType>(LANGUAGE_ROUTE);

const Notification = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ISelectType>();
  const [isPublished, setIsPublished] = useState(false);
  const [startPublish, setStartPublish] = useState("");
  const [finishPublish, setFinishPublish] = useState("");

  const [siteAppOptions, setSiteAppOptions] = useState<ISelectType[]>([]);

  const { sendRequest } = useFetch(
    siteAppExtensionService.siteAppExtensionList
  );

  const loadSiteApp = useCallback(async () => {
    const { data, success } = await sendRequest({ ExtensionCode: "9" });

    if (data && success) {
      setSiteAppOptions(
        data.map((item) => ({ id: item.siteApp.id, name: item.siteApp.name }))
      );
    }
  }, [sendRequest]);

  useEffect(() => {
    loadSiteApp();
  }, [loadSiteApp]);

  const { sendRequest: save, loading: sLoading } = useFetch(createNotification);

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateNotificationById
  );
  const { sendRequest: get, loading: gLoading } = useFetch(getNotificationById);

  const formData = useCallback((v: TNotificationForm): INewNotification => {
    return {
      ...v,
      typeId: v.type.id,
      siteApps: v.siteApps.map((sa) => sa.id),
      notificationBatchContents: v.notificationBatchContents.map((content) => ({
        content: content.content,
        languageId: content.language ? content.language.id : "",
        title: content.title,
      })),
      notificationBatchFilters:
        v.type.code === "3"
          ? v.notificationBatchFilters.map((filter) => ({
              dateStartRules: filter.dateStartRules?.toISOString(),
              ruleCodes: filter.ruleCodes,
            }))
          : v.notificationBatchFilters.map((filter) => ({
              genderId: filter.gender ? filter.gender.id : undefined,
              maximumAge:
                filter.maximumAge !== null &&
                filter.maximumAge !== undefined &&
                filter.maximumAge.length > 0
                  ? +filter.maximumAge
                  : undefined,
              minimumAge:
                filter.minimumAge !== null &&
                filter.minimumAge !== undefined &&
                filter.minimumAge.length > 0
                  ? +filter.minimumAge
                  : undefined,
              profiles: filter.profiles.map((profile) => profile.id),
              users: filter.users.map((user) => user.id),
              isHolder:
                filter.isHolder === "isHolder"
                  ? true
                  : filter.isHolder === "isNotHolder"
                  ? false
                  : null,
              isPayer:
                filter.isPayer === "isPayer"
                  ? true
                  : filter.isPayer === "isNotPayer"
                  ? false
                  : null,
            })),
    };
  }, []);

  const saveHandler = (v: TNotificationForm) => save(formData(v));

  const updateHandler = (v: TNotificationForm, id: string) =>
    update({
      item: formData(v),
      id: id,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TNotificationForm>> => {
      const { data, success } = await get(id);

      if (data) {
        setStatus(data.status);
        setIsPublished(data.isPublished);
        if (data.startPublish) setStartPublish(data.startPublish);
        if (data.finishPublish) setFinishPublish(data.finishPublish);

        const newData: TNotificationForm = {
          ...data,
          notificationBatchContents: data.notificationBatchContents,
          notificationBatchFilters: data.notificationBatchFilters.map(
            (filter) => ({
              ...filter,
              maximumAge: filter.maximumAge ? filter.maximumAge.toString() : "",
              minimumAge: filter.minimumAge ? filter.minimumAge.toString() : "",
              users: filter.users.map((user) => ({
                id: user.id,
                name: user.fullName,
              })),
              isHolder:
                filter.isHolder === null
                  ? "both"
                  : filter.isHolder
                  ? "isHolder"
                  : !filter.isHolder
                  ? "isNotHolder"
                  : "both",
              isPayer:
                filter.isPayer === null
                  ? "both"
                  : filter.isPayer
                  ? "isPayer"
                  : !filter.isPayer
                  ? "isNotPayer"
                  : "both",
              dateStartRules: filter.dateStartRules
                ? new Date(filter.dateStartRules)
                : null,
            })
          ),
        };

        return { data: newData, success };
      }
      return { data, success };
    },
    [get]
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TNotificationForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "notification")}
      schema={notificationSchema}
      subtitle={t(translationPath + "new_notification")}
      subtitleWatchField={"type.name"}
      fields={(type) => (
        <Grid container spacing={2}>
          {type === "update" && (
            <NotificationBatchActions statusCode={status?.code || ""} />
          )}

          {type === "update" && (
            <NotificationInformations
              statusName={status ? status.name : ""}
              isPublished={isPublished}
              startPublish={startPublish}
              finishPublish={finishPublish}
            />
          )}

          <Grid
            item
            xs={12}
            sm={12}
            sx={{ display: "flex", justifyContent: "end" }}
          >
            <SwitchInput name="enable" label={t(translationPath + "enable")} />

            <SwitchInput
              name="sendPush"
              label={
                t(translationPath + "push_notification") ||
                "Generate PushNotification"
              }
            />
          </Grid>

          <NotificationTypeSelect />

          <Grid item xs={12} sm={6}>
            <AutoCompleteMulti
              size="small"
              label={t(translationPath + "site_app")}
              name="siteApps"
              options={siteAppOptions}
            />
          </Grid>

          <NotificationContents service={languageService.list} />

          <NotificationFilters />
        </Grid>
      )}
    />
  );
};

export default Notification;
